import type { components } from "@sagot/api-client";
import { ATTRIBUTE_OBJECTS, ATTRIBUTE_TYPES, type AttributeObject, type AttributeType } from "@sagot/constants";

type Schemas = components["schemas"];
/** The "AI can see this" flags (PDI-007) are optional here: a fixture or an older answer without them reads as off. */
type AiFlag = "ai_visible" | "pii" | "sensitive_hidden_from_ai";

/** `POST /v1/attributes`. */
export type AttributeInput = Schemas["AttributeIn"];
/** `PATCH /v1/attributes/{id}`: only the label and the choices of a `list` change after creation. */
export type AttributePatch = Schemas["AttributePatch"];

/**
 * `GET /v1/attributes`: one custom attribute. The API declares `object` and `type` as plain strings; the
 * list is read through `attributeDefinitions`, which keeps the ones this console knows how to edit, so the
 * two fields here carry the constants' unions.
 */
export type AttributeDefinition = Omit<Schemas["AttributeOut"], "object" | "type" | AiFlag> & Partial<Pick<Schemas["AttributeOut"], AiFlag>> & { object: AttributeObject; type: AttributeType };

/** `GET /v1/sla-policies`: one SLA policy (Institution). */
export type SlaPolicy = Schemas["SlaPolicyOut"];
/** A policy without its id, every field present (an unset optional target is `null`). */
export type SlaPolicyInput = Required<Schemas["SlaPolicyIn"]>;

function definitionOf(row: Schemas["AttributeOut"]): AttributeDefinition | null {
  const object = ATTRIBUTE_OBJECTS.find((candidate) => candidate === row.object);
  const type = ATTRIBUTE_TYPES.find((candidate) => candidate === row.type);
  return object && type ? { ...row, object, type } : null;
}

/** The attribute definitions of a list answer; an object or type from a newer API is left out instead of guessed at. */
export function attributeDefinitions(rows: readonly Schemas["AttributeOut"][]): AttributeDefinition[] {
  return rows.flatMap((row) => definitionOf(row) ?? []);
}

/** Attribute definitions as the condition and step editors group them. */
export function attributesOf(definitions: readonly AttributeDefinition[] | undefined, object: AttributeObject): AttributeDefinition[] {
  return (definitions ?? []).filter((definition) => definition.object === object);
}
